// ── in src/utils/batching.js (or top of DispatchScreen.jsx) ──

/**
 * Simple haversine formula to get kms between two {lat,lng} points
 */
export function haversineKm(a, b) {
  if (!a || !b) return Infinity;
  const toRad = d => (d * Math.PI) / 180;
  const R = 6371; // earth radius km
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const h =
    Math.sin(dLat/2)**2 +
    Math.cos(lat1)*Math.cos(lat2)*Math.sin(dLng/2)**2;

  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * Returns true if `candidateOrder` can be tacked on to the driver’s
 * already‑scheduled `currentOrder`.
 *
 * Assumptions:
 *  • We only batch same‑day orders (guest arrival windows are ≥24h)
 *  • Orders come with candidateOrder.products[].quantity
 *  • driverLoad is the # of items already assigned to that driver today
 *
 * @param {Object} currentOrder     – the driver’s last‐scheduled stop
 * @param {Object} candidateOrder   – a new order you’re considering
 * @param {number} driverLoad       – sum of quantities already on the driver
 * @param {Object} opts
 * @param {number} opts.maxDistanceKm – e.g. 16 km = ~10 miles
 * @param {number} opts.maxLoad       – e.g. 200 items capacity
 */
const MAX_BATCH_DISTANCE_KM = 4;
const MIN_HOURS_BETWEEN = 1;
const MAX_DAILY_LOAD = 100;

// components/Batching.js
export function canBatch(current, next, currentLoad, {
  maxLoad = 20,             // e.g. max total items per driver
  thresholdKm = 16,         // ≈10 mi
  minBufferHours = 2        // must finish ≥2h before next inStockDate
} = {}) {
  if (!current || current.driverID !== next.driverID) return false;

  // 1) distance
  const d = haversineKm(
    current.property.address,
    next.property.address
  );
  if (d > thresholdKm) return false;

  // 2) load
  const nextQty = (next.products||[]).reduce((s,p)=>s+(p.quantity||0),0);
  if (currentLoad + nextQty > maxLoad) return false;

  // 3) timing
  const finishAt = new Date(current.storeArrivalTime).getTime() 
                   + (current.storeSpend * 60_000 || 0); 
  const earliestNext = finishAt + minBufferHours*3600_000;
  if (earliestNext > new Date(next.inStockDate).getTime()) return false;

  return true;
}
